import { useState, type ReactElement } from 'react'
import { CopyText } from 'app/components/CopyText'
import { ButtonAdd } from 'app/components/buttons'
import { confirmActionModal } from '@util'
import {
  isAdminRole,
  isDefined,
  isEmpty,
  isNotEmpty,
  type SmallUser,
  type TeamId,
  type TinyUser,
  type WithCompanyId,
} from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { openModal } from '@mantine/modals'
import { isEmail, isNotEmpty as isFormNotEmpty, useForm } from '@mantine/form'
import { Checkbox, Group, SimpleGrid, Stack, Text, TextInput } from '@mantine/core'
import { useContainerCols, useToastError } from '@hooks'
import {
  getRandomColor,
  Size,
  useAddUser,
  useCompany,
  useExistingUsers,
  usePreference,
  useProfile,
  useRoles,
  useTranslation,
} from '@front/model'
import { FormBlocker, PermissionCheckWrapper, TAlert, TSection } from '@components'
import { DuplicateUsers } from '../DuplicateUsers'
import { UserProfileForm } from './UserProfileForm'
import { UserPreferencesForm } from './UserPreferencesForm'
import { type AddUserFormValues } from './types'
import { TeamMembershipSelect } from './TeamMembershipSelect'
import { AdminRoleToggle } from './AdminRoleToggle'

type FormFooterProps = {
  email: string
  isValidEmail: boolean
  isDirty: boolean
  isAdding: boolean
  isLoadingExistingUsers: boolean
  possibleDuplicates: SmallUser[]
  addAnother: boolean
  onAddAnotherChange: (value: boolean) => void
}

export type AddUserFormProps = Partial<WithCompanyId> & {
  teamId?: TeamId
  onAddUser?: (user: TinyUser) => void
}

export const AddUserForm = ({ onAddUser, teamId, ...props }: AddUserFormProps): ReactElement => {
  const { company: profileCompany } = useProfile()
  const formCols = useContainerCols({ base: 1, sm: 2 })
  const { add, isAdding, error } = useAddUser()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: defaultCloserStatus } = usePreference('closerStatus')
  const { data: managerBasedTeams } = usePreference('managerBasedTeams')
  useToastError(error)
  const companyId = props.companyId ?? profileCompany?.companyId
  const company = useCompany({ companyId })
  const [isAdmin, setIsAdmin] = useState(false)
  const [addAnother, setAddAnother] = useState(false)
  const { data: roles } = useRoles({ companyId })
  const adminRole = roles?.find(isAdminRole)

  const requireManager = managerBasedTeams && !isAdmin
  const userForm = useForm<AddUserFormValues>({
    initialValues: {
      companyId,
      timeZone: company.data?.timeZone,
      lastName: '',
      firstName: '',
      email: '',
      preferredName: '',
      closerStatus: defaultCloserStatus,
      color: getRandomColor(),
      selectedTeamMemberships: teamId ? [{ teamId, roleIds: [] }] : [],
    },
    validate: {
      email: isEmail(t('Invalid email')),
      firstName: isFormNotEmpty(t('First Name is required')),
      lastName: isFormNotEmpty(t('Last Name is required')),
      managerId: requireManager ? isFormNotEmpty(t('Manager is required')) : undefined,
    },
  })
  const { onSubmit, isDirty, values, isValid } = userForm

  const { isLoading: isLoadingExistingUsers, users: possibleDuplicates } = useExistingUsers({
    unsavedUser: values,
  })

  const submit = async (formValues: AddUserFormValues): Promise<void> => {
    const { selectedTeamMemberships, ...unsavedValues } = formValues
    if (isNotEmpty(possibleDuplicates)) {
      const confirmed = await confirmActionModal({
        title: t('Possible Duplicate Users'),
        message: t('There are possible duplicate users. Are you sure you want to continue?'),
        t,
      })
      if (!confirmed) return
    }
    const saved = await add({
      user: {
        ...unsavedValues,
        preferredName: isEmpty(unsavedValues.preferredName) ? undefined : unsavedValues.preferredName,
      },
      teamMemberships: selectedTeamMemberships,
      directRoles: isAdmin && adminRole ? [adminRole.roleId] : undefined,
    })
    if (saved.password) {
      openModal({
        title: t('User Added'),
        children: (
          <Stack>
            <Text>{t("We've emailed their temporary password, but you can also copy it below this one time.")}</Text>
            <CopyText label={t('Password')} value={saved.password} />
          </Stack>
        ),
      })
    } else {
      openModal({
        title: t('User Added'),
        children: (
          <Text>{t('The user was created. We emailed them a setup link to create their password and sign in.')}</Text>
        ),
      })
    }
    if (isDefined(onAddUser)) {
      onAddUser(saved.user)
    } else if (addAnother) {
      userForm.reset()
      userForm.resetDirty()
      setIsAdmin(false)
      showNotification({ message: t('User Added'), color: 'green' })
    } else {
      navigate({ to: `/user/${saved.user.userId}` })
      userForm.resetDirty(formValues)
    }
  }

  return (
    <PermissionCheckWrapper permissionType='user:add' targetId={[teamId, companyId]}>
      <form onSubmit={onSubmit(submit)}>
        <FormBlocker form={userForm} />
        <Stack gap={Size.space['3xl']}>
          <SimpleGrid cols={formCols} type='container'>
            <TSection heading={{ title: t('Profile') }}>
              <UserProfileForm form={userForm} companyId={companyId} requireManager={requireManager} />
              <TextInput
                {...userForm.getInputProps('clientUserId')}
                label={t('External User Id')}
                placeholder={t('12345')}
              />
            </TSection>
            <Stack>
              <TSection heading={{ title: t('Preferences') }}>
                <UserPreferencesForm form={userForm} />
              </TSection>
              <TSection heading={{ title: t('Teams & Roles') }}>
                <AdminRoleToggle companyId={companyId} isAdmin={isAdmin} onChange={setIsAdmin} />
                {!managerBasedTeams && (
                  <TeamMembershipSelect
                    companyId={companyId}
                    value={values.selectedTeamMemberships}
                    onChange={(memberships) => userForm.setFieldValue('selectedTeamMemberships', memberships)}
                  />
                )}
              </TSection>
            </Stack>
          </SimpleGrid>
          <FormFooter
            email={values.email}
            isValidEmail={isValid('email')}
            isDirty={isDirty()}
            isAdding={isAdding}
            isLoadingExistingUsers={isLoadingExistingUsers}
            possibleDuplicates={possibleDuplicates}
            addAnother={addAnother}
            onAddAnotherChange={setAddAnother}
          />
        </Stack>
      </form>
    </PermissionCheckWrapper>
  )
}

const FormFooter = ({
  email,
  isValidEmail,
  isDirty,
  isAdding,
  isLoadingExistingUsers,
  possibleDuplicates,
  addAnother,
  onAddAnotherChange,
}: FormFooterProps): ReactElement => {
  const { t } = useTranslation()
  return (
    <>
      <Group justify='flex-end'>
        {isValidEmail && (
          <TAlert>
            {t('A welcome email will be sent to {{email}} with a setup link to create their password', { email })}
          </TAlert>
        )}
        <Stack align='flex-end' gap='xs'>
          <ButtonAdd disabled={!isDirty || isLoadingExistingUsers} loading={isAdding} type='submit'>
            {t('Add User')}
          </ButtonAdd>
          <Checkbox
            checked={addAnother}
            label={t('Add another')}
            onChange={(event) => onAddAnotherChange(event.currentTarget.checked)}
          />
        </Stack>
      </Group>
      <DuplicateUsers isLoading={isLoadingExistingUsers} users={possibleDuplicates} />
    </>
  )
}
